// src/services/httpUpload.js
import { getIfConfig } from "../config/ifConfig";
import {
  AUTH_INVALID_REASONS,
  emitAuthInvalid,
  isAuthInvalidStatus,
  isCookieCsrfFailure,
} from "./authInvalidEvent";

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getCookie(name) {
  if (typeof document === "undefined" || !name) return null;

  const pattern = new RegExp(`(?:^|; )${escapeRegExp(name)}=([^;]*)`);
  const match = document.cookie.match(pattern);

  return match ? decodeURIComponent(match[1]) : null;
}

const resolveAccessToken = async () => {
  try {
    const mod = await import("../context/AuthContext");
    const getter = mod?.getGlobalAccessToken;

    if (typeof getter !== "function") return null;

    const token = await getter();
    return token || null;
  } catch {
    return null;
  }
};

const hasHeader = (headers, name) => {
  const target = String(name || "").toLowerCase();

  return Object.keys(headers || {}).some(
    (key) => String(key || "").toLowerCase() === target
  );
};

const currentReturnTo = () => {
  if (typeof window === "undefined") return "/";

  return `${window.location.pathname || "/"}${window.location.search || ""}${
    window.location.hash || ""
  }`;
};

const emitReloginRequired = ({
  reason,
  status,
  code,
  source = "httpUpload",
} = {}) => {
  emitAuthInvalid(reason || AUTH_INVALID_REASONS.SESSION_EXPIRED, {
    status,
    code,
    source,
    returnTo: currentReturnTo(),
  });
};

const parseErrorResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  try {
    const text = await response.text();
    return text ? { message: text.slice(0, 200) } : null;
  } catch {
    return null;
  }
};

const readErrorCode = (parsed) => {
  if (!parsed || typeof parsed !== "object") return "";

  const raw =
    parsed?.error?.code ||
    parsed?.code ||
    parsed?.errorCode ||
    parsed?.error?.reason ||
    "";

  return String(raw || "").trim();
};

const readErrorMessage = (parsed, status) => {
  if (!parsed || typeof parsed !== "object") {
    return `Request failed: ${status}`;
  }

  const raw =
    parsed?.error?.message ||
    parsed?.message ||
    parsed?.error ||
    parsed?.detail ||
    "";

  return String(raw || "").trim() || `Request failed: ${status}`;
};

/**
 * uploadMultipart
 * - Uses fetch directly so FormData is sent properly.
 * - Does not set Content-Type; browser sets multipart boundary.
 * - Mirrors apiRequest auth-invalid behaviour for cookie auth.
 */
export const uploadMultipart = async (endpoint, formData, opts = {}) => {
  const {
    requiresAuth = true,
    timeoutMs = 20000,
    extraHeaders = {},
    withCredentials,
    signal: callerSignal,
  } = opts;

  const { API_URL, AUTH_MODE, WITH_CREDENTIALS } = getIfConfig();

  if (!(formData instanceof FormData)) {
    throw new Error("uploadMultipart expects FormData.");
  }

  const cleanEndpoint = String(endpoint || "").trim();
  if (!cleanEndpoint) {
    throw new Error("API endpoint is required.");
  }

  const url = `${API_URL}${cleanEndpoint.startsWith("/") ? "" : "/"}${cleanEndpoint}`;
  const authMode = String(AUTH_MODE || "").toLowerCase();

  const sendCredentials =
    typeof withCredentials === "boolean"
      ? withCredentials
      : authMode === "cookie"
        ? Boolean(WITH_CREDENTIALS)
        : true;

  const headers = {
    Accept: "application/json",
    ...extraHeaders,
  };

  /**
   * Important:
   * Do NOT set Content-Type for FormData.
   * The browser must set multipart/form-data with boundary.
   */

  if (sendCredentials && authMode === "cookie") {
    const csrf = getCookie("if_csrf");

    if (!csrf) {
      emitReloginRequired({
        reason: AUTH_INVALID_REASONS.CSRF_MISSING,
        source: "httpUpload:missing-csrf",
      });

      throw new Error("Session expired. Please sign in again.");
    }

    if (!hasHeader(headers, "X-CSRF-Token")) {
      headers["X-CSRF-Token"] = csrf;
    }
  }

  if (requiresAuth && authMode === "amplify") {
    const accessToken = await resolveAccessToken();

    if (accessToken && !hasHeader(headers, "Authorization")) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  const controller = new AbortController();
  let timedOut = false;

  const onCallerAbort = () => controller.abort();

  if (callerSignal?.addEventListener) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener("abort", onCallerAbort, { once: true });
    }
  }

  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
      signal: controller.signal,
      credentials: sendCredentials ? "include" : "omit",
    });

    if (response.status === 204) return null;

    if (isAuthInvalidStatus(response.status)) {
      const parsed = await parseErrorResponse(response);
      const code = readErrorCode(parsed);

      emitReloginRequired({
        reason:
          response.status === 419
            ? AUTH_INVALID_REASONS.CSRF_FAILED
            : AUTH_INVALID_REASONS.SESSION_EXPIRED,
        status: response.status,
        code,
        source: "httpUpload:auth-invalid-status",
      });

      throw new Error("Session expired. Please sign in again.");
    }

    if (
      isCookieCsrfFailure({
        status: response.status,
        isMutating: true,
        sendCredentials,
        authMode,
      })
    ) {
      const parsed = await parseErrorResponse(response);
      const code = readErrorCode(parsed);

      emitReloginRequired({
        reason: AUTH_INVALID_REASONS.CSRF_FAILED,
        status: response.status,
        code,
        source: "httpUpload:csrf-403",
      });

      throw new Error("Session expired. Please sign in again.");
    }

    if (response.status === 403) {
      throw new Error("Access denied.");
    }

    if (!response.ok) {
      const parsed = await parseErrorResponse(response);
      throw new Error(readErrorMessage(parsed, response.status));
    }

    const contentType = response.headers.get("content-type") || "";

    if (!contentType.includes("application/json")) {
      return null;
    }

    return await response.json();
  } catch (error) {
    if (error?.name === "AbortError") {
      if (timedOut) {
        throw new Error("Request timed out. Please try again.");
      }

      throw new Error("Request was cancelled.");
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);

    if (callerSignal?.removeEventListener) {
      callerSignal.removeEventListener("abort", onCallerAbort);
    }
  }
};